/**
 *
 *  This component serves as a wrapper for React Big Calendar and links it to CalendarBase. It converts
 *  data passed down from CalendarBase into the RBC format as well as mapping user actions from RBC callbacks to
 *  CalendarBase methods. It also does a lot of the heavy lifting for time zone conversion and generating class names
 *  for custom styling.
 *
 * */

import moment from "moment-timezone";
import BigCalendar from "react-big-calendar";
import { Icon } from "semantic-ui-react";

import {
  isMidnight,
  mapAppSlotsToRbcEvents,
  mapRbcEventToAppSlots,
  mapTimeFromTimeZone,
  mapTimeToTimeZone,
} from "common/helpers/calendar";

import "common/css/react-big-calendar.css";
import "common/css/calendar.css";

const NAVIGATE = {
  PREVIOUS: "PREV",
  NEXT: "NEXT",
  TODAY: "TODAY",
};

const noop = () => {};

// A custom toolbar component to replace react-big-calendars default,
// allowing us to include the timezone dropdown and a buffer_time CTA from CalendarBase
const Toolbar =
  (timeZoneSelect, bufferTimeSelect, cta = noop) =>
  (props) => {
    const [_, startMonth, startDay, endMonth, endDay] = props.label.match(
      /(\w+) (\d+) - ([a-zA-Z]* ?)(\d+)/
    );
    const shortStartMonth = moment().month(startMonth).format("MMM");
    const shortEndMonth = moment().month(endMonth).format("MMM");
    const label = `Mon, ${shortStartMonth} ${+startDay} to Fri, ${
      shortEndMonth || shortStartMonth
    } ${+endDay}`;

    return (
      <div className="rbc-toolbar">
        <div>
          <span className="rbc-toolbar-label">{label}</span>
        </div>
        <div className="toolbar-buttons flex items-center">
          {cta()}
          {timeZoneSelect()}
          {bufferTimeSelect()}
          <span className="rbc-btn-group">
            <button
              type="button"
              onClick={() => props.onNavigate(NAVIGATE.PREVIOUS)}
            >
              <Icon fitted name="chevron left" size="small" />
            </button>
            <button
              type="button"
              onClick={() => props.onNavigate(NAVIGATE.TODAY)}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => props.onNavigate(NAVIGATE.NEXT)}
            >
              <Icon fitted name="chevron right" size="small" />
            </button>
          </span>
        </div>
      </div>
    );
  };

const localizer = BigCalendar.momentLocalizer(moment);

// Fix for handling daylight savings. When user changes the week in the calendar view, store the
// timezone offset of days in that week in currentTimzeoneOffset. If you're moving across a daylight
// savings weekend, this can be +/- 60 minutes compared to initialTimezoneOffset. We need to account
// for that offset when handling appointment slot select events.
const initialTimezoneOffset = new Date().getTimezoneOffset();
let currentTimezoneOffset = initialTimezoneOffset;

const AppointmentSlots = (props) => {
  const { calendar } = props;
  // modal view displays two fewer hours of time, so we want to be further down
  const initalScrollTime = new Date(1970, 0, 0, props.isModal ? 10 : 9);

  const events = mapAppSlotsToRbcEvents(
    [...props.appointmentSlots],
    props.timeZone
  );

  const adjustedCurrentTime = () =>
    mapTimeToTimeZone(new Date(), props.timeZone);

  const dayPropGetter = (day) =>
    +mapTimeFromTimeZone(day, props.timeZone) < Date.now()
      ? { className: "past-day-header" }
      : {};

  // requestToDelete exists when we are rescheduling a confirmed request and have to mark the original interview time
  const eventPropGetter = (event, requestToDelete) => {
    const className = [];

    if (+mapTimeFromTimeZone(event.start, props.timeZone) < Date.now()) {
      className.push("past");
    }

    const momentStart = moment(event.start);
    const momentEnd = moment(event.end);

    // event will only have a height of one slot, so we dont show rbc event label
    if (momentEnd <= momentStart.add(30, "minutes")) {
      className.push("short");
    }

    // lets us add a border between adjacent events when needed
    if (event.adjacentEventAbove) {
      className.push("adjacent-event-above");
    }

    if (event.taken) {
      className.push("taken");
    } else {
      className.push("available");
    }

    if (requestToDelete && event.interview_id === requestToDelete.id) {
      className.push("to-delete");
    }

    return { className };
  };

  const slotPropGetter = (time) =>
    +mapTimeFromTimeZone(time, props.timeZone) > Date.now()
      ? { className: "selectable-slot" }
      : {};

  const handleMidnight = (event) => {
    if (isMidnight(event.end)) {
      // make it end at 11:59:59 so BigCalendar can display it properly
      return new Date(event.end.getTime() - 1000);
    }

    return event.end;
  };

  const handleSelectRBCEvent = (event) => {
    if (calendar && calendar.google_synced === true) {
      return;
    }
    if (event.start < adjustedCurrentTime() || event.taken) {
      return;
    }

    props.removeSlots(
      mapRbcEventToAppSlots(
        event,
        props.timeZone,
        currentTimezoneOffset - initialTimezoneOffset
      )
    );
  };

  const handleSelectRBCSlot = (event) => {
    if (calendar && calendar.google_synced === true) {
      return;
    }
    if (+mapTimeFromTimeZone(event.start, props.timeZone) < Date.now()) {
      return;
    }

    props.createSlots(
      mapRbcEventToAppSlots(
        event,
        props.timeZone,
        currentTimezoneOffset - initialTimezoneOffset
      )
    );
  };

  // if it's Friday evening, display next week by default
  const defaultDate = () => {
    const currentDate = mapTimeToTimeZone(new Date(), props.timeZone);

    if (currentDate.getDay() === 5 && currentDate.getHours() >= 12) {
      const nextSunday = currentDate.getDate() + 2;
      currentDate.setDate(nextSunday);
    }

    return currentDate;
  };

  return (
    <BigCalendar
      components={{
        toolbar: Toolbar(
          props.timeZoneSelect,
          props.bufferTimeSelect,
          props.cta
        ),
      }}
      dayPropGetter={dayPropGetter}
      defaultDate={defaultDate()}
      defaultView={BigCalendar.Views.WORK_WEEK}
      endAccessor={handleMidnight}
      eventPropGetter={(event) => eventPropGetter(event, props.requestToDelete)}
      events={events}
      getNow={adjustedCurrentTime}
      localizer={localizer}
      onRangeChange={(days) => {
        currentTimezoneOffset = days[0].getTimezoneOffset();
      }}
      onSelectEvent={handleSelectRBCEvent}
      onSelectSlot={handleSelectRBCSlot}
      selectable={
        calendar && calendar.google_synced === true ? false : "ignoreEvents"
      }
      scrollToTime={initalScrollTime}
      slotPropGetter={slotPropGetter}
      views={[BigCalendar.Views.WORK_WEEK]}
      className={calendar && calendar.google_synced && "calendar-synced"}
    />
  );
};

export default AppointmentSlots;
